import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, ArrowRight, Sparkles, GraduationCap } from 'lucide-react';
import * as Icons from 'lucide-react';

import { KB_SECTIONS, KB_GUIDES, guideSearchText } from '@/data/knowledgeBase';
import type { Guide, GuideLevel } from '@/data/knowledgeBase';
import { AppRoutes } from '@/constants/routes';
import { ShareLinkButton } from '@/components/help/ShareLinkButton';
import { cn } from '@/utils/cn';

/**
 * The knowledge base index.
 *
 * Two ways in, because people arrive with two different questions. Someone who knows the shape of
 * the system browses by menu section; someone stuck mid-task types what went wrong. The search
 * covers guide BODIES, not just titles — "bounced" and "payable to CASH" are sentences inside a
 * guide, and a title-only search would find neither.
 */

const LevelPill = ({ level }: { level: GuideLevel }) => (
  <span className={cn(
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
    level === 'Basics'
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
      : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  )}>
    {level === 'Basics' ? <Sparkles className="h-3 w-3" /> : <GraduationCap className="h-3 w-3" />}
    {level}
  </span>
);

const GuideCard = ({ guide, href, shareable }: { guide: Guide; href: string; shareable: boolean }) => (
  <Link
    to={href}
    className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-colors
               hover:border-brand-300 hover:bg-brand-50/30 focus:outline-none focus-visible:ring-2
               focus-visible:ring-brand-500 dark:border-slate-700 dark:bg-slate-800
               dark:hover:border-brand-600 dark:hover:bg-slate-700/50"
  >
    <div className="mb-2 flex items-start justify-between gap-3">
      <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white">{guide.title}</h3>
      <span className="flex shrink-0 items-center gap-1">
        <LevelPill level={guide.level} />
        {shareable && <ShareLinkButton slug={guide.slug} compact />}
      </span>
    </div>
    <p className="flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{guide.summary}</p>
    <div className="mt-4 flex items-center justify-between">
      <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 dark:text-brand-400">
        Read guide <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
      <span className="text-xs text-slate-400">Menu: {guide.menuPath}</span>
    </div>
  </Link>
);

interface Props {
  /**
   * 'public' is the signed-out mirror at /kb: same guides, links that stay inside the mirror.
   * The two share one component so the public copy cannot quietly fall behind the real one.
   */
  variant?: 'app' | 'public';
}

export const KnowledgeBasePage = ({ variant = 'app' }: Props = {}) => {
  const isPublic = variant === 'public';
  const guideHref = (slug: string) =>
    (isPublic ? AppRoutes.KB_PUBLIC_GUIDE : AppRoutes.HELP_GUIDE).replace(':slug', slug);

  const [query,   setQuery]   = useState('');
  const [level,   setLevel]   = useState<'All' | GuideLevel>('All');
  const [section, setSection] = useState<string>('all');

  // Indexed once: the body text of every guide, lowercased.
  const index = useMemo(
    () => KB_GUIDES.map(g => ({ guide: g, text: guideSearchText(g) })),
    []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return index
      .filter(({ guide, text }) =>
        (level === 'All' || guide.level === level) &&
        (section === 'all' || guide.section === section) &&
        (!q || text.includes(q)))
      .map(r => r.guide);
  }, [index, query, level, section]);

  const searching = query.trim().length > 0;

  const countFor = (key: string) =>
    KB_GUIDES.filter(g => g.section === key && (level === 'All' || g.level === level)).length;

  return (
    <div className="p-6">
      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 p-8 text-white">
        {/* A soft disc, echoing the platform's help surface. Decorative only. */}
        <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" />
        <div className="relative max-w-3xl">
          <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80">
            <BookOpen className="h-3.5 w-3.5" /> myLedger ONE · Knowledge base
          </p>
          <h1 className="text-3xl font-bold leading-tight">How can we help?</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/80">
            {isPublic
              ? 'Step-by-step guides to myLedger, organised by menu section. Reading needs no account; '
                + 'opening the screens they describe does.'
              : 'Step-by-step guides to myLedger, organised by menu section, with links straight to the '
                + 'screens they describe.'}
          </p>

          {!isPublic && (
            <p className="mt-4 flex flex-wrap items-center gap-3 text-xs text-white/70">
              <ShareLinkButton label="Copy public link to this knowledge base"
                className="border-white/30 bg-white/10 text-white hover:border-white/60 hover:text-white
                           dark:border-white/30 dark:bg-white/10 dark:text-white" />
              Anyone with the link can read the guides without signing in.
            </p>
          )}

          <div className="relative mt-6 max-w-xl">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search guides, or paste a message you saw… (e.g. already reserved, payable to CASH)"
              aria-label="Search guides"
              className="w-full rounded-xl border-0 bg-white py-3 pl-11 pr-4 text-sm text-slate-900
                         shadow-lg outline-none placeholder:text-slate-400
                         focus:ring-2 focus:ring-white/60 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* ── Browse by section ───────────────────────────────────────────────── */}
      {!searching && section === 'all' && level === 'All' && (
        <>
          <h2 className="mb-3 mt-8 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-gray-400">
            Browse by menu section
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {KB_SECTIONS.map(s => {
              const Icon = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[s.icon]
                ?? Icons.BookOpen;
              return (
                <button key={s.key} onClick={() => setSection(s.key)}
                  className="group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 text-left
                             transition-colors hover:border-brand-300 hover:bg-brand-50/30
                             dark:border-slate-700 dark:bg-slate-800 dark:hover:border-brand-600">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold text-slate-900 dark:text-white">{s.title}</span>
                    <span className="block text-xs text-slate-500 dark:text-gray-400">
                      {countFor(s.key)} guide{countFor(s.key) === 1 ? '' : 's'}
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 dark:text-slate-600" />
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        {(['All', 'Basics', 'Advanced'] as const).map(l => (
          <button key={l} onClick={() => setLevel(l)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              level === l
                ? 'bg-brand-600 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
            )}>
            {l === 'All' ? 'All levels' : l}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={() => setSection('all')}
          className={cn(
            'rounded-lg px-3 py-1.5 text-sm transition-colors',
            section === 'all'
              ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
              : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
          )}>
          All sections
        </button>
        {KB_SECTIONS.map(s => (
          <button key={s.key} onClick={() => setSection(s.key)}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors',
              section === s.key
                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
            )}>
            {s.title}
            <span className={cn('rounded px-1.5 text-xs',
              section === s.key ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700')}>
              {countFor(s.key)}
            </span>
          </button>
        ))}
      </div>

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      {searching && (
        <p className="mt-6 text-sm text-slate-500 dark:text-gray-400">
          {results.length === 0
            ? <>Nothing matches “{query}”. Try a word from the message you saw, or browse a section above.</>
            : <>{results.length} guide{results.length === 1 ? '' : 's'} matching “{query}”</>}
        </p>
      )}

      {KB_SECTIONS
        .filter(s => section === 'all' || section === s.key)
        .map(s => {
          const inSection = results.filter(g => g.section === s.key);
          if (inSection.length === 0) return null;
          return (
            <section key={s.key} className="mt-6">
              <h2 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-gray-400">
                {s.title}
              </h2>
              <p className="mb-3 text-sm text-slate-500 dark:text-gray-400">{s.description}</p>
              <div className="grid gap-4 lg:grid-cols-2">
                {inSection.map(g => (
                  <GuideCard key={g.slug} guide={g} href={guideHref(g.slug)} shareable={!isPublic} />
                ))}
              </div>
            </section>
          );
        })}
    </div>
  );
};
